import React, { FC, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import MobileNavToggle from './MobileNavToggle';
import './use-case-editor.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SimUser {
  id: string;
  name: string;
  companyName: string;
}

interface SimulationRun {
  runId: string;
  targetUserId: string;
  userName: string | null;
  status: 'pending' | 'running' | 'done' | 'error';
  output: any | null;
  summary: string | null;
  systemPrompt: string | null;
  error: string | null;
}

interface QuestionAnswer {
  topic: string;
  framing: string;
  answer: string;
}

const MAX_WIDTH = 900;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #ddd',
  borderRadius: 8,
  fontFamily: 'Geist, sans-serif',
  fontSize: 14,
  outline: 'none',
};

// ─── Main Component ──────────────────────────────────────────────────────────

const SuggestionSimulation: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [secret, setSecret] = useState('');
  const [secretUnlocked, setSecretUnlocked] = useState(false);

  const [users, setUsers] = useState<SimUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [dayOffset, setDayOffset] = useState(0);
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState<string | null>(null);
  const pollingRef = useRef<Set<string>>(new Set());

  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>([]);

  const [error, setError] = useState<string | null>(null);

  // ─── Auth ────────────────────────────────────────────────────────────────

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
      'x-simulation-secret': secret,
    };
  }, [getAccessTokenSilently, secret]);

  // ─── Unlock & fetch users ────────────────────────────────────────────────

  const handleUnlock = async () => {
    setError(null);
    setUsersLoading(true);
    try {
      const headers = await authHeaders();
      const res = await api.get('/suggestion-simulation/users', { headers });
      if (res.data.success) {
        setUsers(res.data.users);
        setSecretUnlocked(true);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setError('Invalid secret or access denied.');
      } else {
        setError('Failed to load users.');
      }
    } finally {
      setUsersLoading(false);
    }
  };

  // ─── Polling ──────────────────────────────────────────────────────────────

  const pollRun = useCallback(
    async (runId: string) => {
      if (pollingRef.current.has(runId)) return;
      pollingRef.current.add(runId);

      const poll = async () => {
        try {
          const headers = await authHeaders();
          const res = await api.get(`/suggestion-simulation/run/${runId}`, {
            headers,
          });
          if (res.data.success) {
            const updated = res.data as SimulationRun;
            setRuns((prev) =>
              prev.map((r) => (r.runId === runId ? updated : r)),
            );
            if (updated.status === 'done' || updated.status === 'error') {
              pollingRef.current.delete(runId);
              return;
            }
          }
        } catch {
          // keep polling on transient errors
        }
        setTimeout(poll, 3000);
      };
      poll();
    },
    [authHeaders],
  );

  // ─── Run simulation ──────────────────────────────────────────────────────

  const handleRun = async () => {
    if (!selectedUserId) {
      setError('Select a user first.');
      return;
    }
    setError(null);
    const selectedUser = users.find((u) => u.id === selectedUserId);

    const simulatedAnswers = questionAnswers
      .filter((qa) => qa.answer.trim())
      .map((qa) => ({ topic: qa.topic, answer: qa.answer.trim() }));

    try {
      const headers = await authHeaders();
      const res = await api.post(
        '/suggestion-simulation/run',
        {
          targetUserId: selectedUserId,
          dayOffset: dayOffset || undefined,
          simulatedAnswers: simulatedAnswers.length
            ? simulatedAnswers
            : undefined,
        },
        { headers },
      );
      if (res.data.success) {
        const { runId } = res.data;
        const placeholder: SimulationRun = {
          runId,
          targetUserId: selectedUserId,
          userName: selectedUser?.name ?? null,
          status: 'pending',
          output: null,
          summary: null,
          systemPrompt: null,
          error: null,
        };
        setRuns((prev) => [placeholder, ...prev]);
        setExpandedRun(runId);
        pollRun(runId);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to start simulation');
    }
  };

  // ─── Collect questions from a completed run ──────────────────────────────

  const collectQuestionsFromRun = (run: SimulationRun) => {
    const questions = run.output?.questions ?? [];
    if (questions.length === 0) return;
    setQuestionAnswers(
      questions.map((q: any) => ({
        topic: q.topic || '',
        framing: q.framing || '',
        answer: '',
      })),
    );
    setDayOffset((prev) => prev + 1);
  };

  // ─── Render: secret gate ─────────────────────────────────────────────────

  if (!secretUnlocked) {
    return (
      <div className='use-case-editor' style={{ maxWidth: MAX_WIDTH }}>
        <MobileNavToggle />
        <div className='header'>
          <h2>Suggestion Simulation</h2>
        </div>

        {error && <div className='msg error'>{error}</div>}

        <div className='edit-form' style={{ maxWidth: 400 }}>
          <div className='form-group'>
            <label>Enter simulation secret</label>
            <input
              type='password'
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder='secret sights'
              onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
            />
          </div>
          <div className='form-actions'>
            <button
              className='primary-btn'
              onClick={handleUnlock}
              disabled={usersLoading || !secret}
            >
              {usersLoading ? 'Checking...' : 'Unlock'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: simulation interface ────────────────────────────────────────

  return (
    <div className='use-case-editor' style={{ maxWidth: MAX_WIDTH }}>
      <MobileNavToggle />
      <div className='header'>
        <h2>Suggestion Simulation</h2>
      </div>

      {error && <div className='msg error'>{error}</div>}

      {/* Controls */}
      <div className='edit-form' style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div className='form-group' style={{ flex: 2, minWidth: 200 }}>
            <label>User</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={inputStyle}
            >
              <option value=''>— Select a user —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.companyName})
                </option>
              ))}
            </select>
          </div>
          <div className='form-group' style={{ flex: 0, minWidth: 120 }}>
            <label>Day offset</label>
            <input
              type='number'
              value={dayOffset}
              onChange={(e) => setDayOffset(parseInt(e.target.value) || 0)}
              style={{ ...inputStyle, width: 120 }}
              min={0}
              max={365}
            />
          </div>
        </div>

        {/* Simulated question answers */}
        {questionAnswers.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <label style={{ fontWeight: 600, fontSize: 13 }}>
                Simulated answers ({questionAnswers.length} questions from
                previous run)
              </label>
              <button
                className='secondary-btn'
                style={{ fontSize: 11, padding: '4px 10px' }}
                onClick={() => setQuestionAnswers([])}
              >
                Clear
              </button>
            </div>
            {questionAnswers.map((qa, i) => (
              <div
                key={i}
                style={{
                  background: '#fefce8',
                  border: '1px solid #fde68a',
                  borderRadius: 8,
                  padding: '10px 14px',
                  marginBottom: 8,
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {qa.framing || qa.topic}
                </div>
                <input
                  type='text'
                  placeholder='Type a simulated answer...'
                  value={qa.answer}
                  onChange={(e) => {
                    const next = [...questionAnswers];
                    next[i] = { ...next[i], answer: e.target.value };
                    setQuestionAnswers(next);
                  }}
                  style={{ ...inputStyle, fontSize: 13 }}
                />
              </div>
            ))}
          </div>
        )}

        <div className='form-actions' style={{ marginTop: 16 }}>
          <button
            className='primary-btn'
            onClick={handleRun}
            disabled={!selectedUserId}
          >
            Run Simulation{dayOffset ? ` (+${dayOffset}d)` : ''}
          </button>
        </div>
      </div>

      {/* Results */}
      {runs.map((run) => (
        <div key={run.runId} className='edit-form' style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
            }}
            onClick={() =>
              setExpandedRun(expandedRun === run.runId ? null : run.runId)
            }
          >
            <strong style={{ flex: 1 }}>
              {run.userName ?? run.targetUserId} — {run.runId.slice(0, 8)}
            </strong>
            <span
              style={{
                fontSize: 12,
                color:
                  run.status === 'error'
                    ? '#dc2626'
                    : run.status === 'done'
                      ? '#16a34a'
                      : '#d97706',
                fontWeight: 600,
              }}
            >
              {run.status === 'pending' || run.status === 'running'
                ? 'running...'
                : run.status === 'done'
                  ? 'done'
                  : 'error'}
            </span>
            <span style={{ fontSize: 12, color: '#888' }}>
              {expandedRun === run.runId ? '▼' : '▶'}
            </span>
          </div>

          {expandedRun === run.runId && (
            <div style={{ marginTop: 16 }}>
              {(run.status === 'pending' || run.status === 'running') && (
                <div style={{ fontSize: 13, color: '#92400e' }}>
                  Running... polling every 3s
                </div>
              )}
              {run.status === 'error' && run.error && (
                <div className='msg error'>{run.error}</div>
              )}

              {run.summary && (
                <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                  <strong>Summary:</strong> {run.summary}
                </div>
              )}

              {/* Suggestions */}
              {run.output?.suggestions && run.output.suggestions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
                    Suggestions ({run.output.suggestions.length})
                  </h4>
                  {run.output.suggestions.map((s: any, i: number) => (
                    <div
                      key={i}
                      style={{
                        background: '#f9fafb',
                        border: '1px solid #e5e5e5',
                        borderRadius: 8,
                        padding: '10px 14px',
                        marginBottom: 8,
                        fontSize: 13,
                      }}
                    >
                      <div>
                        <strong>{s.title}</strong>
                      </div>
                      <div style={{ color: '#888', fontSize: 12 }}>
                        archetype: {s.archetypeId} | urgency: {s.urgency} |
                        confidence: {s.confidence}
                      </div>
                      {s.overview && (
                        <div style={{ marginTop: 4, color: '#555' }}>
                          {s.overview}
                        </div>
                      )}
                      {s.prefilledQuickChatMessage && (
                        <div
                          style={{
                            marginTop: 6,
                            padding: '6px 10px',
                            background: '#e0f2fe',
                            borderRadius: 6,
                            fontSize: 12,
                            color: '#0369a1',
                          }}
                        >
                          QuickChat: {s.prefilledQuickChatMessage}
                        </div>
                      )}
                      {s.prework?.directions && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            color: '#7c3aed',
                          }}
                        >
                          Pre-work: {s.prework.directions}
                        </div>
                      )}
                      {s.evidenceChecked && s.evidenceChecked.length > 0 && (
                        <details style={{ marginTop: 6, fontSize: 12 }}>
                          <summary style={{ cursor: 'pointer', color: '#888' }}>
                            Evidence ({s.evidenceChecked.length} checks)
                          </summary>
                          <div style={{ marginTop: 4 }}>
                            {s.evidenceChecked.map((e: any, j: number) => (
                              <div
                                key={j}
                                style={{
                                  color: '#555',
                                  marginBottom: 2,
                                }}
                              >
                                [{e.source}] {e.query} → {e.summary}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Questions */}
              {run.output?.questions && run.output.questions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
                    Questions ({run.output.questions.length})
                  </h4>
                  {run.output.questions.map((q: any, i: number) => (
                    <div
                      key={i}
                      style={{
                        background: '#fefce8',
                        border: '1px solid #fde68a',
                        borderRadius: 8,
                        padding: '10px 14px',
                        marginBottom: 8,
                        fontSize: 13,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{q.framing}</div>
                      {q.reason && (
                        <div
                          style={{
                            marginTop: 4,
                            color: '#6b7280',
                            fontSize: 12,
                            fontStyle: 'italic',
                          }}
                        >
                          Reason: {q.reason}
                        </div>
                      )}
                      <div
                        style={{
                          color: '#92400e',
                          fontSize: 12,
                          marginTop: 2,
                        }}
                      >
                        topic: {q.topic} | urgency: {q.urgency}
                      </div>
                      {q.because && (
                        <div
                          style={{
                            marginTop: 4,
                            color: '#92400e',
                            fontSize: 12,
                          }}
                        >
                          because:{' '}
                          {Array.isArray(q.because)
                            ? q.because.join(', ')
                            : q.because}
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    className='secondary-btn'
                    style={{ marginTop: 4, fontSize: 12 }}
                    onClick={() => collectQuestionsFromRun(run)}
                  >
                    Use these questions for next run
                  </button>
                </div>
              )}

              {/* Reminders */}
              {run.output?.reminders && run.output.reminders.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
                    Reminders ({run.output.reminders.length})
                  </h4>
                  {run.output.reminders.map((r: any, i: number) => (
                    <div
                      key={i}
                      style={{
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: 8,
                        padding: '10px 14px',
                        marginBottom: 8,
                        fontSize: 13,
                      }}
                    >
                      <div>
                        <strong>{r.hint}</strong>
                      </div>
                      <div style={{ color: '#888', fontSize: 12 }}>
                        due: {r.dueDate}
                        {r.archetypeId && ` | archetype: ${r.archetypeId}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {run.status === 'done' &&
                !run.output?.suggestions?.length &&
                !run.output?.questions?.length &&
                !run.output?.reminders?.length && (
                  <div style={{ fontSize: 13, color: '#888' }}>
                    No suggestions, questions, or reminders produced.
                    <details style={{ marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer' }}>
                        Raw output (debug)
                      </summary>
                      <pre
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          background: '#f3f4f6',
                          padding: 8,
                          borderRadius: 6,
                          overflow: 'auto',
                          maxHeight: 300,
                        }}
                      >
                        {run.output
                          ? JSON.stringify(run.output, null, 2)
                          : 'output is null/undefined'}
                      </pre>
                    </details>
                  </div>
                )}

              {/* System Prompt toggle */}
              {run.systemPrompt && (
                <>
                  <button
                    className='secondary-btn'
                    onClick={() =>
                      setShowPrompt(showPrompt === run.runId ? null : run.runId)
                    }
                    style={{ marginTop: 8 }}
                  >
                    {showPrompt === run.runId
                      ? 'Hide System Prompt'
                      : 'Show System Prompt'}
                  </button>
                  {showPrompt === run.runId && (
                    <pre
                      style={{
                        marginTop: 12,
                        background: '#1e1e1e',
                        color: '#d4d4d4',
                        padding: 16,
                        borderRadius: 8,
                        fontSize: 12,
                        overflow: 'auto',
                        maxHeight: 500,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {run.systemPrompt}
                    </pre>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SuggestionSimulation;
